/**
 * "מי עידכן מה" — the two names every operational record carries about itself.
 *
 * The append-only ledger (`src/db/audit-store.ts`) already answers the forensic
 * question: open a record's היסטוריית שינויים and every change ever made to it
 * is there, frozen, with the name and the job the person held at the time. What
 * it does *not* answer is the glance question — a מ"פ scrolling a roster of
 * twenty-eight vehicles wants to know who last touched each row without opening
 * twenty-eight cards.
 *
 * So the record itself carries two fields beside its two stamps:
 *
 * | field | written by | reads as |
 * | --- | --- | --- |
 * | `createdByName` | the create path, once | `נוצר ע״י אשר אמר · 09:14` |
 * | `updatedByName` | every write path, every time | `עודכן ע״י אשר אמר · 16:05` |
 *
 * They are *names*, not ids, and they are copied onto the row rather than joined
 * at read time — the same reasoning the ledger is built on. A row that arrives
 * from another device through the cloud mirror carries them along with the rest
 * of its payload, which is what makes the indicator answerable on a phone that
 * was not the one the change was made on.
 *
 * A record written before these fields existed carries neither, and the
 * describe functions return `null` for it rather than inventing an author. The
 * screens simply render nothing — an unsigned line is worse than no line.
 *
 * Pure: no Dexie, no network, no browser. The verification harness executes the
 * decisions here directly.
 */
import type { User } from '../types/user';

/** The two fields, named once, so the schema and the checks agree on them. */
export const AUDIT_ACTOR_FIELDS = ['createdByName', 'updatedByName'] as const;

/** What every audited record carries about who touched it. */
export interface AuditActorNames {
  /** The name of the operator that opened the record. */
  createdByName?: string;
  /** The name of the operator that last wrote to it. */
  updatedByName?: string;
}

/** An audited record, as the describe functions read one. */
export interface AuditStamped extends AuditActorNames {
  createdAt?: string;
  updatedAt?: string;
}

/** How the indicator opens each of the two lines. */
export const AUDIT_CREATED_PREFIX = 'נוצר ע״י';
export const AUDIT_UPDATED_PREFIX = 'עודכן ע״י';

/**
 * The name to file an actor under.
 *
 * An account with no name on it — which should not happen, and does when a
 * record arrives from a build that stored less — is filed as `—` rather than as
 * an empty string, so a stamp is never silently indistinguishable from no stamp
 * at all.
 */
export function actorName(actor: Pick<User, 'fullName'> | null | undefined): string {
  return String(actor?.fullName ?? '').trim() || '—';
}

/** The fields a create writes: both names, because a create is also a write. */
export function stampCreated(actor: Pick<User, 'fullName'> | null | undefined): AuditActorNames {
  const name = actorName(actor);
  return { createdByName: name, updatedByName: name };
}

/**
 * The field a write moves. `createdByName` is deliberately not among them —
 * whoever opened the record opened it, and no later edit may rewrite that.
 */
export function stampUpdated(actor: Pick<User, 'fullName'> | null | undefined): AuditActorNames {
  return { updatedByName: actorName(actor) };
}

/**
 * The time a change is read at: `16:05` for something that happened today, and
 * `12/06 16:05` for anything older.
 *
 * A bare clock time is what the unit wants on a change made this shift, and it
 * is actively misleading on one made last week — so the date joins it exactly
 * when it starts to matter. An unparseable stamp is handed back untouched: it is
 * still more informative than the empty string it would otherwise become.
 */
export function formatAuditClock(iso: string, now: number = Date.now()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const pad = (part: number) => String(part).padStart(2, '0');
  const clock = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  const today = new Date(now);
  const sameDay =
    at.getFullYear() === today.getFullYear() &&
    at.getMonth() === today.getMonth() &&
    at.getDate() === today.getDate();
  return sameDay ? clock : `${pad(at.getDate())}/${pad(at.getMonth() + 1)} ${clock}`;
}

/**
 * `עודכן ע״י אשר אמר · 16:05`, or `null` when the record does not say.
 *
 * The clock is the record's own `updatedAt`, so the line always names the moment
 * of the change it attributes and never "now".
 */
export function describeLastUpdate(
  record: AuditStamped | null | undefined,
  now: number = Date.now(),
): string | null {
  const name = String(record?.updatedByName ?? '').trim();
  if (!name) return null;
  const at = record?.updatedAt || record?.createdAt;
  return at ? `${AUDIT_UPDATED_PREFIX} ${name} · ${formatAuditClock(at, now)}` : `${AUDIT_UPDATED_PREFIX} ${name}`;
}

/** `נוצר ע״י אשר אמר · 09:14`, or `null` when the record does not say. */
export function describeCreatedBy(
  record: AuditStamped | null | undefined,
  now: number = Date.now(),
): string | null {
  const name = String(record?.createdByName ?? '').trim();
  if (!name) return null;
  const at = record?.createdAt;
  return at ? `${AUDIT_CREATED_PREFIX} ${name} · ${formatAuditClock(at, now)}` : `${AUDIT_CREATED_PREFIX} ${name}`;
}

/**
 * Both lines, for a card that has room for them — the creation first, then the
 * last change, and only when the two are actually different. A record nobody has
 * touched since it was opened says so once.
 */
export function describeAuditTrail(
  record: AuditStamped | null | undefined,
  now: number = Date.now(),
): string[] {
  const created = describeCreatedBy(record, now);
  const updated = describeLastUpdate(record, now);
  if (created && updated && record?.createdAt === record?.updatedAt) return [created];
  return [created, updated].filter((line): line is string => Boolean(line));
}
